import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Landmark, Pencil, ChevronDown, Trash2, RotateCcw } from "lucide-react";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Pill, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = ["Current", "Savings", "OD / CC", "Loan"];
const blank = { bank_name: "HDFC Bank", account_number: "", account_holder: "", account_type: "Current", opening_balance: "0.00", tally_ledger: "", entity_kind: "business" };

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">{label}</Label>{children}</div>;
const Pick = ({ value, onChange, items, testid }) => (
  <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
);

function AccountDialog({ edit, open, setOpen }) {
  const { meta, refreshMasters, businesses, biz } = useApp();
  const [f, setF] = useState(blank);
  const [added, setAdded] = useState(0);
  useEffect(() => { if (open) { setF(edit ? { ...blank, ...edit, account_number: "" } : blank); setAdded(0); } }, [open, edit]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const save = async (again) => {
    try {
      if (edit) {
        await api.patch(`/accounts/${edit.id}`, { account_holder: f.account_holder, account_type: f.account_type, opening_balance: f.opening_balance, tally_ledger: f.tally_ledger, entity_kind: f.entity_kind });
        toast.success("Account updated");
      } else {
        await api.post("/accounts", f);
        toast.success(`${f.bank_name} account added`);
        setAdded((n) => n + 1);
      }
      refreshMasters();
      if (again) setF((s) => ({ ...s, account_number: "", tally_ledger: "", opening_balance: "0.00" })); else setOpen(false);
    } catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl" data-testid="account-dialog">
        <DialogHeader><DialogTitle>{edit ? `Edit ${edit.bank_name} ${edit.masked}` : "Add bank account"}</DialogTitle></DialogHeader>
        <div className="text-xs text-muted-foreground">Business: {businesses.find((b) => b.id === biz)?.name}{added > 0 && ` · ${added} added in this session`}</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <F label="Bank">{edit ? <Input value={f.bank_name} disabled /> : <Pick value={f.bank_name} onChange={set("bank_name")} items={meta.banks} testid="account-bank-select" />}</F>
          <F label="Account number">{edit ? <Input value={edit.masked} disabled /> : <Input value={f.account_number} onChange={set("account_number")} placeholder="Full number (stored encrypted)" data-testid="account-number-input" />}</F>
          <F label="Account holder"><Input value={f.account_holder} onChange={set("account_holder")} data-testid="account-holder-input" /></F>
          <F label="Account type"><Pick value={f.account_type} onChange={set("account_type")} items={TYPES} testid="account-type-select" /></F>
          <F label="Business or personal"><Pick value={f.entity_kind} onChange={set("entity_kind")} items={["business", "personal"]} testid="account-entity-select" /></F>
          <F label="Opening balance (₹)"><Input className="num" value={f.opening_balance} onChange={set("opening_balance")} data-testid="account-opening-input" /></F>
          <div className="sm:col-span-2"><F label="Tally bank ledger (exact name; defaults to bank + last 4 digits)"><Input value={f.tally_ledger} onChange={set("tally_ledger")} data-testid="account-ledger-input" /></F></div>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {!edit && <Button variant="outline" onClick={() => save(true)} data-testid="account-save-another-button">Save & add another</Button>}
          <Button onClick={() => save(false)} data-testid="account-save-button">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function History({ id }) {
  const [s, setS] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/statements", { params: { account_id: id } }).then((r) => setS(r.data)); }, [id]);
  if (!s) return <div className="p-3 text-xs text-muted-foreground">Loading…</div>;
  if (!s.length) return <div className="p-3 text-xs text-muted-foreground">No statements uploaded for this account yet.</div>;
  return s.map((x) => (
    <button key={x.id} onClick={() => nav(`/statements/${x.id}`)} className="flex w-full items-center justify-between gap-3 border-t px-1 py-2 text-left text-xs hover:text-primary" data-testid="account-statement-row">
      <span className="truncate">{x.filename}</span><span className="num text-muted-foreground">{fmtDate(x.period_start)} – {fmtDate(x.period_end)}</span><Pill s={x.status} />
    </button>
  ));
}

function RemoveDialog({ acct, onClose }) {
  const { refreshMasters } = useApp();
  const [reason, setReason] = useState("");
  useEffect(() => { setReason(""); }, [acct]);
  const go = async () => {
    try { await api.post(`/accounts/${acct.id}/remove`, { reason }); toast.success(`${acct.bank_name} ${acct.masked} removed`); refreshMasters(); onClose(); }
    catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <Dialog open={!!acct} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="remove-account-dialog">
        <DialogHeader><DialogTitle>Remove {acct?.bank_name} {acct?.masked}?</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>The account will be hidden from lists, uploads and auto-detection. Its {acct?.statement_count} statement(s) and {acct?.transaction_count} transaction(s) are <b>kept</b> for your audit trail and Tally history.</p>
          <p>You can restore it any time from “Removed accounts”.</p>
        </div>
        <Input placeholder="Reason (required), e.g. account closed" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="remove-account-reason" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="remove-account-cancel">Cancel</Button>
          <Button variant="destructive" disabled={!reason.trim()} onClick={go} data-testid="remove-account-confirm">Remove account</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Removed({ tick }) {
  const { refreshMasters, can } = useApp();
  const [list, setList] = useState([]);
  const load = () => api.get("/accounts", { params: { archived: true } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [tick]);
  const restore = async (a) => {
    try { await api.post(`/accounts/${a.id}/restore`); toast.success(`${a.bank_name} ${a.masked} restored`); load(); refreshMasters(); } catch (e) { toast.error(await errMsg(e)); }
  };
  if (!list.length) return <div className="text-sm text-muted-foreground">No removed accounts.</div>;
  return list.map((a) => (
    <div key={a.id} className="flex flex-wrap items-center gap-3 border-b py-3 text-sm last:border-0" data-testid="removed-account-row">
      <div className="min-w-0 flex-1"><div className="font-medium">{a.bank_name} <span className="num text-muted-foreground">{a.masked}</span></div>
        <div className="text-xs text-muted-foreground">Removed {a.archived_at?.slice(0, 10)} by {a.archived_by} — {a.archive_reason} · {a.statement_count} statement{a.statement_count === 1 ? "" : "s"} and {a.transaction_count} transaction{a.transaction_count === 1 ? "" : "s"} kept</div></div>
      {can("admin") && <Button size="sm" variant="outline" onClick={() => restore(a)} data-testid="restore-account-button"><RotateCcw className="mr-1 h-3 w-3" />Restore</Button>}
    </div>
  ));
}

export default function BankAccounts() {
  const { accounts, can } = useApp();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const banks = [...new Set(accounts.map((a) => a.bank_name))];
  return (
    <div>
      <PageHeader title="Bank Accounts" sub="Add as many accounts as you need — several at the same bank too. Each keeps its own statements, reconciliation status and Tally bank ledger. Numbers are encrypted; only the last 4 digits are shown.">
        {can("admin") && <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-bank-account-button"><Plus className="mr-2 h-4 w-4" />Add bank account</Button>}
      </PageHeader>
      {accounts.length === 0 && <Empty title="No bank accounts yet" sub="Add your first account to start uploading statements." />}
      {banks.map((bank) => (
        <div key={bank} className="mb-8">
          <h3 className="mb-3 flex items-center gap-2 font-semibold"><Landmark className="h-4 w-4 text-primary" />{bank} <span className="text-xs font-normal text-muted-foreground">{accounts.filter((a) => a.bank_name === bank).length} account(s)</span></h3>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {accounts.filter((a) => a.bank_name === bank).map((a) => (
              <div key={a.id} className="panel p-5 fade-up" data-testid="bank-account-card">
                <div className="flex items-start justify-between gap-2">
                  <div><div className="num text-lg font-semibold">{a.masked}</div><div className="text-xs text-muted-foreground">{a.account_holder} · {a.account_type} · {a.entity_kind}</div></div>
                  <Pill s={a.recon_status === "none" ? "no_statements" : a.recon_status} testid="bank-account-recon-status" />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                  <div><div className="label-xs">Opening</div><div className="num">{inr(a.opening_balance)}</div></div>
                  <div><div className="label-xs">Latest closing</div><div className="num">{inr(a.closing_balance)}</div><div className="text-[10px] text-muted-foreground">{a.closing_date ? `as of ${fmtDate(a.closing_date)}` : ""}</div></div>
                  <div className="col-span-2"><div className="label-xs">Tally ledger</div><div className="truncate">{a.tally_ledger}</div></div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <button onClick={() => setExpanded(expanded === a.id ? null : a.id)} className="flex items-center gap-1 text-xs text-primary" data-testid="account-history-toggle">
                    {a.statement_count} statement(s)<ChevronDown className={`h-3 w-3 transition-transform ${expanded === a.id ? "rotate-180" : ""}`} /></button>
                  {can("admin") && <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEdit(a); setOpen(true); }} data-testid="edit-bank-account-button"><Pencil className="mr-1 h-3 w-3" />Edit</Button>
                    <Button size="sm" variant="ghost" className="text-rose-500 hover:text-rose-600" onClick={() => setRemoving(a)} data-testid="remove-bank-account-button"><Trash2 className="mr-1 h-3 w-3" />Remove</Button>
                  </div>}
                </div>
                {expanded === a.id && <div className="mt-2"><History id={a.id} /></div>}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="mt-4">
        <button onClick={() => setShowRemoved((s) => !s)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="toggle-removed-accounts">
          Removed accounts<ChevronDown className={`h-3 w-3 transition-transform ${showRemoved ? "rotate-180" : ""}`} /></button>
        {showRemoved && <div className="panel mt-3 p-4"><Removed tick={accounts.length} /></div>}
      </div>
      <AccountDialog edit={edit} open={open} setOpen={setOpen} />
      <RemoveDialog acct={removing} onClose={() => setRemoving(null)} />
    </div>
  );
}
